/**
 * RetuneRoomButton — operator-only long-press in the bottom-right
 * corner.
 *
 * Mirrors the LeaveBoothButton mechanic (1.5s hold, hairline radial
 * fill, no visible chrome) but ends at /booth with the previous
 * event's pairing memory cleared.
 *
 * Constitutional locks:
 *   • Only rendered on the launcher view + QR continuity view —
 *     never during a guest capture flow. A wedding attendant must
 *     NEVER discover this mid-capture.
 *   • No button chrome until the press is in progress.
 *   • Editorial copy revealed only as the ring fills, so a guest
 *     who happens to touch the corner sees nothing surprising.
 *   • Theme is preserved (it's a venue config, not an event config);
 *     only `booth_os_paired_event_id` + `event_name` are cleared.
 *
 * Behaviour preserved verbatim from the previous inline component.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SERIF } from './boothShellAtoms';

const HOLD_MS = 1500;

const RetuneRoomButton = ({ theme }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const startRef = useRef(0);

  const tick = useCallback(() => {
    const elapsed = Date.now() - startRef.current;
    const p = Math.min(1, elapsed / HOLD_MS);
    setProgress(p);
    if (p < 1) {
      timerRef.current = requestAnimationFrame(tick);
    } else {
      // Clear ONLY the pairing memory — theme persists so the next
      // night inherits the operator's chosen atmosphere.
      try {
        localStorage.removeItem('booth_os_paired_event_id');
        localStorage.removeItem('booth_os_paired_event_name');
      } catch {}
      // Brief dissolve then walk to /booth. We don't try to force
      // re-fullscreen — Chrome / Safari both prohibit it across
      // navigations without a fresh user gesture. The fullscreen
      // affordance on /booth-os already handles re-entry.
      setTimeout(() => navigate('/booth'), 280);
    }
  }, [navigate]);

  const begin = () => {
    startRef.current = Date.now();
    setProgress(0);
    timerRef.current = requestAnimationFrame(tick);
  };
  const cancel = () => {
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    timerRef.current = null;
    setProgress(0);
  };

  return (
    <button
      type="button"
      onPointerDown={begin}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      data-testid="booth-retune-longpress"
      aria-label="Hold to retune the room"
      style={{
        position: 'fixed', right: 18, bottom: 18,
        width: 56, height: 56,
        background: 'transparent', border: 'none',
        cursor: 'pointer', padding: 0,
        zIndex: 5,
        outline: 'none', appearance: 'none',
      }}
    >
      {progress > 0 && (
        <>
          {/* Hairline ring sweeping clockwise from the top. */}
          <span style={{
            position: 'absolute', inset: 4,
            borderRadius: '50%',
            background: `conic-gradient(${theme.gold} ${progress * 360}deg, transparent 0deg)`,
            WebkitMask: 'radial-gradient(circle, transparent 56%, #000 58%)',
                    mask: 'radial-gradient(circle, transparent 56%, #000 58%)',
            opacity: 0.85,
          }} />
          {/* Editorial copy revealed only as the press is in flight. */}
          <span style={{
            position: 'absolute',
            right: 64, bottom: 18,
            whiteSpace: 'nowrap',
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12, color: theme.gold,
            letterSpacing: '0.08em',
            opacity: Math.min(1, progress * 1.4),
            pointerEvents: 'none',
          }}>
            Choose another night ✦
          </span>
        </>
      )}
    </button>
  );
};

export default RetuneRoomButton;
